import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const ForgotPassword = () => {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);

    const { error } = await resetPassword(email);

    if (error) {
      toast.error(error);
    } else {
      setEmailSent(true);
      toast.success('Password reset email sent!');
    }

    setLoading(false);
  };

  if (emailSent) {
    return (
      <div className="min-h-screen pt-28 pb-16 flex items-center justify-center px-6" data-testid="forgot-password-success">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="font-serif text-2xl font-semibold text-[#0A0A0A] mb-4">
            Check Your Email
          </h1>
          <p className="text-[#737373] mb-8">
            We've sent a password reset link to <span className="font-medium text-[#0A0A0A]">{email}</span>. 
            Click the link in the email to reset your password.
          </p>
          <p className="text-sm text-[#737373] mb-8">
            Didn't receive the email? Check your spam folder or{' '}
            <button 
              onClick={() => setEmailSent(false)} 
              className="text-[#C9A962] hover:underline"
            >
              try again
            </button>
          </p>
          <Link 
            to="/login"
            className="inline-flex items-center gap-2 text-sm text-[#737373] hover:text-[#0A0A0A] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-16 flex items-center justify-center px-6" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        {/* Back Link */}
        <Link 
          to="/login"
          className="inline-flex items-center gap-2 text-sm text-[#737373] hover:text-[#0A0A0A] transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to login
        </Link>

        {/* Header */}
        <div className="mb-8">
          <h1 className="font-serif text-3xl font-semibold text-[#0A0A0A] mb-2">
            Forgot Password?
          </h1>
          <p className="text-[#737373]">
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#737373]" />
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="pl-10 rounded-none h-12 border-[#E5E5E5] focus:border-[#0A0A0A] focus:ring-0"
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#0A0A0A] text-white hover:bg-[#262626] rounded-none h-12"
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </Button>
        </form>

        {/* Help Text */}
        <p className="mt-8 text-center text-sm text-[#737373]">
          Remember your password?{' '}
          <Link to="/login" className="text-[#C9A962] hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
